import { Button, Typography } from "@suraasa/placebo-ui"
import Logo from "assets/logos/mini.webp"
import { db } from "firebaseInit"
import { Clock, FileText, Video } from "lucide-react"
import { PropsWithChildren } from "react"
import { Link } from "react-router-dom"
import { USER_TYPE } from "utils/constants"

import { useMaintenance } from "./hooks/useMaintenance"

export default function MaintenanceMode({ children }: PropsWithChildren) {
  const { isMaintenanceActive, timeLeft } = useMaintenance({
    firebaseDb: db,
    platform: USER_TYPE,
  })

  if (!isMaintenanceActive) {
    return <>{children}</>
  }

  const hasEnded =
    timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 p-2">
      <div className="w-full max-w-2xl rounded-xl bg-white p-3 shadow-lg">
        <div className="mb-3 flex justify-center">
          <img src={Logo} alt="Suraasa Logo" className="h-12 w-auto" />
        </div>
        <div>
          <Typography
            variant="title2"
            className="mb-0.5 flex items-center justify-center text-center"
          >
            Website Under Maintenance
          </Typography>
        </div>
        <div>
          <p className="mb-2 text-center">
            We love making things better for you. We are currently working on
            some updates to improve your experience on Suraasa.
          </p>
          <div className="mb-3 rounded-lg bg-primary/10 p-2">
            <Typography variant="title3" className="mb-2 flex items-center">
              <Clock className="mr-2 shrink-0" />
              Estimated time until completion:
            </Typography>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <span className="text-3xl font-bold">
                  {hasEnded ? "--" : timeLeft.hours.toString().padStart(2, "0")}
                </span>
                <p className="text-sm">Hours</p>
              </div>
              <div>
                <span className="text-3xl font-bold">
                  {hasEnded
                    ? "--"
                    : timeLeft.minutes.toString().padStart(2, "0")}
                </span>
                <p className="text-sm">Minutes</p>
              </div>
              <div>
                <span className="text-3xl font-bold">
                  {hasEnded
                    ? "--"
                    : timeLeft.seconds.toString().padStart(2, "0")}
                </span>
                <p className="text-sm">Seconds</p>
              </div>
            </div>
          </div>
          <h3 className="mb-1 text-lg font-semibold">
            In the meantime you can:
          </h3>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Link to="https://suraasa.com/blog" className="w-full">
              <Button
                variant="outlined"
                startAdornment={<FileText />}
                className="w-full"
              >
                Read Blogs
              </Button>
            </Link>
            <Link to="https://suraasa.co/youtube" className="w-full">
              <Button
                variant="outlined"
                startAdornment={<Video />}
                className="w-full"
              >
                Visit our YouTube Channel
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
